import React, { useCallback, useMemo } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import { disconnectionSchema, disconnectionUISchema } from '../../schemas/disconnectionSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert/ExpiringAlert';

const DisconnectionForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext],
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.disconnection_type':
          return { ...error, message: 'Disconnection Type is a required property' };
        case '.appointment_date':
          return { ...error, message: 'Appointment Date must be in yyyyMMdd format (e.g. 20250612)' };
        case '.earliest_appointment_time':
          return { ...error, message: 'Earliest Appointment Time must be in HHmmss format (e.g. 120000)' };
        case '.latest_appointment_time':
          return { ...error, message: 'Latest Appointment Time must be in HHmmss format (e.g. 160000)' };
        default:
          return error;
      }
    });
  }, []);

  const customValidate = useCallback((formData, errors) => {
    if (formData.appointment_date) {
      const raw = formData.appointment_date;
      if (!/^\d{8}$/.test(raw)) {
        errors.appointment_date.addError('Must be in yyyyMMdd format (e.g. 20250612)');
      } else {
        const year = parseInt(raw.slice(0, 4), 10);
        const month = parseInt(raw.slice(4, 6), 10) - 1;
        const day = parseInt(raw.slice(6, 8), 10);
        const parsed = new Date(year, month, day);
        const isValid =
          parsed.getFullYear() === year &&
          parsed.getMonth() === month &&
          parsed.getDate() === day;
        if (!isValid) {
          errors.appointment_date.addError('Must be a valid calendar date in yyyyMMdd format');
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (parsed < today) {
            errors.appointment_date.addError('Appointment Date must be today or in the future');
          }
        }
      }
    }
    return errors;
  }, []);

  const transformFormDataToPayload = (formData) => {
    const payload = {
      mpan_core: parseInt(formData.mpan_core, 10),
      disconnection_type: formData.disconnection_type,
    };
    if (formData.additional_information) payload.additional_information = formData.additional_information;
    if (formData.appointment_date) payload.appointment_date = formData.appointment_date;
    if (formData.earliest_appointment_time) payload.earliest_appointment_time = formData.earliest_appointment_time;
    if (formData.latest_appointment_time) payload.latest_appointment_time = formData.latest_appointment_time;
    return payload;
  };

  const uiSchemaWithWidgets = useMemo(() => ({
    ...disconnectionUISchema,
    mpan_core: {
      ...disconnectionUISchema.mpan_core,
      'ui:widget': (props) => <MeterPointSelector {...props} placeholder='Select MPAN' />,
    },
  }), []);

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='disconnection-form'>
      <Form
        schema={disconnectionSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        validate={customValidate}
        showErrorList={false}
        onSubmit={({ formData }) => {
          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);

          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.StartDisconnection' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Disconnection request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting disconnection request:', error);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div')),
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DisconnectionForm;
